import { CheckCircle2, Filter, ListChecks, Search, UsersRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { apiErrorMessage } from "../../../api/client.js";
import { Button } from "../../../design/components/Button.js";
import { Alert, EmptyState, Skeleton } from "../../../design/components/Feedback.js";
import { ResponsiveSheet } from "../../../design/components/ResponsiveSheet.js";
import { api, type AssignmentStudio as AssignmentStudioData, type AssignmentStudioSelection, type Conference } from "../../api.js";

const selectionKey = (submissionId: string, reviewerId: string) => `${submissionId}:${reviewerId}`;

function reasonLabel(reason: string) {
  const [kind, value = ""] = reason.split(":", 2);
  if (kind === "topic_match") return value === "none" ? "No topic match" : `Topic match: ${value.split(",").join(", ")}`;
  if (kind === "workload") return `${value} active review${value === "1" ? "" : "s"}`;
  if (kind === "expertise") return Number(value) > 0 ? `Expertise score ${value}` : "No bid recorded";
  return reason;
}

export function AssignmentStudio({ conference, onUpdated }: { conference: Conference; onUpdated: () => void }) {
  const [studio, setStudio] = useState<AssignmentStudioData | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"ALL" | "NEEDS_REVIEWERS" | "COMPLETE">("ALL");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = async () => {
    setError("");
    try {
      setStudio(await api.assignmentStudioPreview(conference.id, conference.organization_id));
    } catch (reason) {
      setError(apiErrorMessage(reason, "The assignment studio could not be loaded."));
    }
  };

  useEffect(() => { void load(); }, [conference.id, conference.organization_id]);

  const submissions = useMemo(() => (studio?.submissions ?? []).filter((submission) => {
    const matchesQuery = `${submission.reference} ${submission.title}`.toLocaleLowerCase().includes(query.toLocaleLowerCase());
    const needsReviewers = submission.assigned_count < submission.target_count;
    return matchesQuery && (filter === "ALL" || (filter === "NEEDS_REVIEWERS" ? needsReviewers : !needsReviewers));
  }), [studio, query, filter]);

  const selections = useMemo<AssignmentStudioSelection[]>(() => {
    if (!studio?.review_round) return [];
    return studio.submissions.flatMap((submission) => submission.candidates
      .filter((candidate) => selected.has(selectionKey(submission.id, candidate.reviewer_id)))
      .map((candidate) => ({
        submission_id: submission.id,
        reviewer_id: candidate.reviewer_id,
        invitation_id: candidate.invitation_id,
        review_round_id: studio.review_round!.id,
      })));
  }, [studio, selected]);

  const chooseSuggested = () => {
    if (!studio) return;
    const next = new Set<string>();
    for (const submission of studio.submissions) {
      const needed = Math.max(0, submission.target_count - submission.assigned_count);
      submission.candidates
        .filter((candidate) => candidate.eligible && candidate.active_assignments < (candidate.preferred_capacity || candidate.expected_work || 1))
        .slice(0, needed)
        .forEach((candidate) => next.add(selectionKey(submission.id, candidate.reviewer_id)));
    }
    setSelected(next);
  };

  const apply = async () => {
    if (!selections.length) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await api.assignmentStudioApply(conference.id, conference.organization_id, selections);
      setNotice(`${result.created} reviewer assignment${result.created === 1 ? "" : "s"} applied${result.skipped ? `; ${result.skipped} already existed` : ""}.`);
      setSelected(new Set());
      setConfirming(false);
      await load();
      onUpdated();
    } catch (reason) {
      setError(apiErrorMessage(reason, "The selected assignments could not be applied."));
    } finally {
      setBusy(false);
    }
  };

  if (error && !studio) return <Alert tone="danger">{error}</Alert>;
  if (!studio) return <div aria-label="Loading assignment studio"><Skeleton /><Skeleton /><Skeleton /></div>;

  return <div className="review-assignment-studio">
    <header>
      <div><span>Event Pro</span><h2>Reviewer Assignment Studio</h2><p>Review explained suggestions, balance workloads, and apply assignments deliberately.</p></div>
      <Button type="button" variant="secondary" onClick={chooseSuggested}><ListChecks size={16} />Select suggested reviewers</Button>
    </header>
    {error ? <Alert tone="danger">{error}</Alert> : null}
    {notice ? <Alert tone="success">{notice}</Alert> : null}
    {!studio.review_round ? <Alert tone="neutral">Publish a review round before assigning reviewers.</Alert> : null}
    <div className="assignment-studio-toolbar">
      <label><Search size={16} /><span className="sr-only">Search submissions</span><input aria-label="Search submissions" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search submissions" /></label>
      <label><Filter size={16} /><span className="sr-only">Filter submissions</span><select aria-label="Filter submissions" value={filter} onChange={(event) => setFilter(event.target.value as typeof filter)}><option value="ALL">All submissions</option><option value="NEEDS_REVIEWERS">Needs reviewers</option><option value="COMPLETE">Target reached</option></select></label>
      <div><strong>{studio.submissions.filter((item) => item.assigned_count < item.target_count).length}</strong><span>need reviewers</span></div>
    </div>
    {submissions.length ? <div className="assignment-studio-list">{submissions.map((submission) => <section key={submission.id} className="assignment-studio-submission">
      <header><div><span>{submission.reference}</span><h3>{submission.title}</h3></div><strong className={submission.assigned_count >= submission.target_count ? "is-complete" : ""}>{submission.assigned_count} of {submission.target_count} assigned</strong></header>
      {submission.candidates.length ? <div className="assignment-studio-candidates">{submission.candidates.map((candidate, index) => {
        const key = selectionKey(submission.id, candidate.reviewer_id);
        const capacity = candidate.preferred_capacity || candidate.expected_work || 1;
        const overloaded = candidate.active_assignments >= capacity;
        return <label key={candidate.reviewer_id} className={selected.has(key) ? "is-selected" : ""}>
          <input type="checkbox" checked={selected.has(key)} disabled={!studio.review_round || !candidate.eligible} onChange={(event) => setSelected((current) => { const next = new Set(current); if (event.target.checked) next.add(key); else next.delete(key); return next; })} />
          <span className="assignment-candidate-rank">{index + 1}</span>
          <span className="assignment-candidate-person"><strong>{candidate.name || candidate.email}</strong><small>{candidate.email}</small>{candidate.expertise_tags.length ? <span>{candidate.expertise_tags.join(" · ")}</span> : null}</span>
          <span className="assignment-candidate-reasons">{candidate.reasons.map((reason) => <small key={reason}>{reasonLabel(reason)}</small>)}</span>
          <span className={overloaded ? "assignment-workload is-over" : "assignment-workload"}><UsersRound size={15} />{candidate.active_assignments}/{capacity}</span>
        </label>;
      })}</div> : <Alert tone="neutral">No conflict-free accepted reviewers are eligible for this submission.</Alert>}
    </section>)}</div> : <EmptyState title="No submissions match" description="Change the search or filter to see more submissions." />}
    <footer className="assignment-studio-footer"><span>{selections.length ? `${selections.length} assignment${selections.length === 1 ? "" : "s"} selected` : "Select candidates or use the suggested set."}</span><Button type="button" disabled={!selections.length} onClick={() => setConfirming(true)}>Review {selections.length || ""} assignment{selections.length === 1 ? "" : "s"}</Button></footer>
    <ResponsiveSheet open={confirming} title="Apply reviewer assignments?" layout="modal" onClose={() => !busy && setConfirming(false)} footer={<><Button type="button" variant="secondary" disabled={busy} onClick={() => setConfirming(false)}>Cancel</Button><Button type="button" busy={busy} onClick={() => void apply()}><CheckCircle2 size={16} />Apply assignments</Button></>}>
      <div className="assignment-confirmation"><p>Conferis will recheck permissions, track scope, accepted invitations, and conflicts before creating anything.</p><strong>{selections.length} assignment{selections.length === 1 ? "" : "s"} will be applied.</strong></div>
    </ResponsiveSheet>
  </div>;
}
